import { FileInputButton } from '@/components/FileInput';
import { ImageCropComponent } from '@/components/images/ImageCropComponent';
import { AppContext } from '@/contexts/app.context';
import { BoneTag, CreateBoneTag } from '@/generated';
import { boneTagApi } from '@/generated/clients';
import { uploadFile } from '@/utils/api-helpers';
import { objectHasId } from '@/utils/create-mode-helper';
import { Photo } from '@mui/icons-material';
import { Autocomplete, Box, Button, Dialog, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import { useContext, useEffect, useState } from 'react';

type EditBoneTagProps = {
  initialBoneTag?: BoneTag | CreateBoneTag;
  boneTagUpdated?: (muscleTag: BoneTag | CreateBoneTag) => void;
  onClose?: () => void;
  open: boolean;
};

export const EditBoneTagModal = (props: EditBoneTagProps) => {
  const { initialBoneTag, open } = props;
  const [imageFile, setImageFile] = useState<File | null>();
  const [boneTag, setBoneTag] = useState<BoneTag | CreateBoneTag | undefined>(initialBoneTag);
  const isEditMode = boneTag ? objectHasId(boneTag) : false;

  useEffect(() => {
    setBoneTag(initialBoneTag);
  }, [initialBoneTag]);

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={() => {
        if (props.onClose) props.onClose();
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
        Edit Bone Tag
      </DialogTitle>
      <DialogContent>
        <Box sx={{ marginTop: 2, marginBottom: 2 }}>
          <ImageCropComponent imageFile={imageFile} onImageFileChanged={setImageFile} imageId={initialBoneTag?.imageId} />
        </Box>
        <FileInputButton accept=".png,.jpg,.jpeg" startIcon={<Photo />} onFilePicked={(f) => setImageFile(f)}>
          Change Image
        </FileInputButton>
        <Stack>
          <TextField
            value={boneTag?.name}
            placeholder="Tag Name"
            label="Tag Name"
            aria-label="Title"
            aria-describedby="basic-addon1"
            sx={{ my: 1 }}
            onChange={(e: any) => {
              if (boneTag) setBoneTag({ ...boneTag, name: e.target.value });
            }}
          />
          <TextField
            multiline
            value={boneTag?.description}
            placeholder="Description"
            label="Description"
            aria-label="Description"
            aria-describedby="basic-addon1"
            sx={{ my: 1 }}
            onChange={(e: any) => {
              if (boneTag) setBoneTag({ ...boneTag, description: e.target.value });
            }}
          />
          <BoneTagAutocomplete
            selectedBoneTags={boneTag?.groupsBoneTagIds ?? []}
            setSelectedBoneTags={(bt) => {
              if (boneTag) setBoneTag({ ...boneTag, groupsBoneTagIds: bt });
            }}
          />
        </Stack>

        <Box sx={{ m: 1 }}>
          <Button
            onClick={async () => {
              if (boneTag !== undefined) {
                let bt = boneTag;
                if (imageFile) {
                  const fileId = await uploadFile(imageFile);
                  bt = { ...boneTag, imageId: fileId };
                }

                if (objectHasId(bt)) {
                  await boneTagApi.boneTagUpdate({ boneTag: bt });
                  if (props.boneTagUpdated) props?.boneTagUpdated(bt);
                  setImageFile(null);
                } else {
                  await boneTagApi.boneTagCreate({ createBoneTag: bt });
                  if (props.boneTagUpdated) props?.boneTagUpdated(bt);
                  setImageFile(null);
                }
              }

              setBoneTag(undefined);
              if (props.onClose) props.onClose();
            }}
          >
            {isEditMode ? 'Save' : 'Create'}
          </Button>
          <Button
            onClick={() => {
              setBoneTag(undefined);
              if (props.onClose) props.onClose();
            }}
          >
            Close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default function BoneTagAutocomplete(props: { selectedBoneTags: Array<string>; setSelectedBoneTags: (ids: Array<string>) => void }) {
  const context = useContext(AppContext);
  const { selectedBoneTags, setSelectedBoneTags } = props;

  return (
    <Autocomplete
      multiple
      autoHighlight
      id="tags-outlined"
      defaultValue={context.state?.boneTagList.filter((bt) => selectedBoneTags.some((id) => id === bt.id))}
      onChange={(event, boneTags) => {
        setSelectedBoneTags(boneTags.map((bt) => bt.id));
      }}
      options={context.state?.boneTagList ?? []}
      getOptionLabel={(option) => option.name}
      filterSelectedOptions
      sx={{ mt: 1, minWidth: 200 }}
      renderInput={(params) => <TextField variant="outlined" {...params} label={`Bone Tags`} placeholder={selectedBoneTags.length === 0 ? `Bone Tags` : undefined} />}
    />
  );
}
